import React, { Fragment } from "react";
import { createPortal } from "react-dom";
import { find, map, reduce, split, times, trim } from "lodash";
import { useLocation } from "react-router";
import { useTranslation } from "react-i18next";
import { Row } from "@/shared/ui/Blocks";
import { Icon, IconName } from "@/shared/ui/Icon";
import Config from "./breadcrumbs.config";
import { cn } from "@/shared/utils/shadcn";
import { EntitySelectDropdown } from "../EntitySelectDropdown";
import useOrgNavigate from "@/shared/utils/router/useOrgNavigate";

interface BreadcrumbItemProps {
  id?: string;
  label: string;
  icon?: IconName;
  path?: string;
  options?: BreadcrumbItemProps[];
}

interface BreadcrumbsProps {
  items?: BreadcrumbItemProps[];
}

const PORTAL_ID = "wis-breadcrumbs-portal";

export function BreadcrumbsPortal() {
  return <div id={PORTAL_ID} className="flex gap-4" />;
}

const BreadcrumbItem: React.FC<BreadcrumbItemProps> = (item) => {
  const { t } = useTranslation();
  const navigate = useOrgNavigate();

  if (item.options) {
    return (
      <EntitySelectDropdown
        side="bottom"
        align="start"
        options={map(item.options, (option) => ({
          id: option?.id || option.label,
          name: option.label,
          path: option.path,
        }))}
        selectedOption={item?.id || item.label}
        searchEnabled={item.options.length > 4}
        onSelect={(o) => navigate(o.path)}
      >
        <div className={cn("flex items-center gap-2 mr-3", "cursor-pointer")}>
          {item.icon && <Icon icon={item.icon} size="sm" color="darkGrey" />}
          <p className={cn("text-sm", !!item?.path && "font-semibold")}>
            {t(item.label)}
          </p>
          <Icon icon="Expand" size="xs" color="darkGrey" />
        </div>
      </EntitySelectDropdown>
    );
  }

  return (
    <div
      className={cn(
        "flex items-center gap-2 mr-3",
        !!item.path && "cursor-pointer"
      )}
      onClick={() => {
        item?.path && navigate(item?.path);
      }}
    >
      {item.icon && <Icon icon={item.icon} size="sm" color="darkGrey" />}
      <p
        className={cn(
          "text-sm max-w-52 truncate text-ellipsis overflow-hidden line-clamp-1",
          !!item?.path && "font-semibold"
        )}
      >
        {t(item.label)}
      </p>
    </div>
  );
};

const getDefaultCrumb = (path: string) => {
  const crumb = find(Config, (c) => trim(c.path, "/") === trim(path, "/"));

  return crumb;
};

const getDefaultCrumbs = (path: string) =>
  reduce(
    split(path, "/"),
    (acc, _crumb) => {
      const crumb = getDefaultCrumb(_crumb);

      if (crumb) {
        return [...acc, crumb];
      }
      return acc;
    },
    [] as BreadcrumbItemProps[]
  );

export const Breadcrumbs: React.FC<BreadcrumbsProps> = ({ items }) => {
  const location = useLocation();

  const defaultCrumbs = getDefaultCrumbs(location.pathname);

  const headerContainer = document.getElementById(PORTAL_ID);

  if (!headerContainer) return null;

  const breadcrumbs = [...defaultCrumbs, ...(items || [])];

  function BreadCrumbRow() {
    return (
      <Row className="ml-4 items-center">
        {map(breadcrumbs, (item, index) => (
          <Fragment key={`breadcrumb-${index}`}>
            <span className="text-grey-400 text-xl mr-3 leading-3">/</span>
            <BreadcrumbItem {...item} />
          </Fragment>
        ))}
      </Row>
    );
  }

  return createPortal(<BreadCrumbRow />, document.getElementById(PORTAL_ID)!);
};
